/**
 * Streams a project folder as a zip via the shared assets-api zip-download
 * client (`shared/api/zipDownload`). StreamSaver keeps the archive off the tab's
 * heap — a multi-GB folder would otherwise OOM.
 *
 * Two failure paths, one toast each: manifest 4xx/5xx flows through
 * `problem-details.interceptor.ts` (single actionable toast, so we stay quiet);
 * the stream POST bypasses axios, so we toast it inline here.
 *
 * Takes the platform project id rather than reading `folder.projectId`, which is
 * the internal id — the manifest endpoint scopes its permission check on the
 * platform one, so passing the internal id 403s.
 */
import type { FolderResponseDto } from '@inference.engine/workspace-api-client/web';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { notificationService } from '@/lib/services/notification.service';
import {
  sanitizeZipFilename,
  streamZipToDisk,
  type ZipDownloadRequestBody,
} from '@/shared/api/zipDownload';
import { getFolderDownloadManifest } from './project-folders.api';

export { getFolderDownloadManifest };

export function useDownloadFolder({ platformProjectId }: { platformProjectId: string }) {
  const { t } = useTranslation('projects');

  return useMutation<undefined, Error, FolderResponseDto>({
    mutationFn: async (folder) => {
      const manifest = await getFolderDownloadManifest(platformProjectId, folder.id);

      const requestBody: ZipDownloadRequestBody = {
        folderName: manifest.folderName,
        assets: manifest.assets.map((asset) => ({
          assetId: asset.assetId,
          archivePath: asset.archivePath,
          fileSize: asset.fileSize,
        })),
      };

      const filename = sanitizeZipFilename(manifest.folderName);
      try {
        await streamZipToDisk(requestBody, filename);
      } catch (error) {
        // The stream POST skips the axios interceptor, so surface its failure
        // here. Manifest failures above already toast via the interceptor.
        notificationService.showError(
          t('manageProjectFolders.useDownloadFolder.downloadFailedTryAgain'),
        );
        throw error;
      }
      return undefined;
    },
  });
}
